import { useState } from "react";
import Button from "@imminently/decisively-ui/Button";
import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";
import Tabs from "@imminently/decisively-ui/Tabs";
import { Typography } from "@mui/material";
import { JsonEditor } from "@/common/JsonEditor";
import { FileDropZone } from "@/components/FileDropzone/FileDropzone";
import { SpreadsheetPreview } from "@/components/SpreadsheetPreview/SpreadsheetPreview";
import {
  buildContactCreatePayload,
  mapContactData,
  mapEntityData,
  mapOrgModelData,
  mapRequestData,
  parseCSV,
} from "@packages/commons";

type CsvPreviewFailedRow = {
  row: Record<string, any>;
  rowNumber: number;
  error: string;
};

type CsvPreviewResult = {
  file: string;
  result: any;
  originalData?: string;
  error?: string;
  failedRows?: CsvPreviewFailedRow[];
};

interface CSVPreviewProps {
  values: any;
  release: any;
  previewType?: "request" | "contact" | "entity" | "orgmodel";
  setIsProcessingCsv: (value: boolean) => void;
  setCsvPreview: (preview: CsvPreviewResult | null) => void;
  csvPreview: CsvPreviewResult | null;
  isProcessingCsv: boolean;
}

export const CSVPreview = ({
  values,
  release,
  previewType = "request",
  setIsProcessingCsv,
  setCsvPreview,
  csvPreview,
  isProcessingCsv,
}: CSVPreviewProps) => {
  const isMultilineParentToken = (mappingDef: any, tokenValue: unknown): boolean => {
    const token = String(tokenValue || "").trim();
    if (!token) return false;
    if (token.toLowerCase() === "global") return true;
    const mappingValue = mappingDef?.entityMapping?.mapping?.[token];
    if (!mappingValue) return false;
    const mappedEntity = typeof mappingValue === "object"
      ? String(mappingValue?.entity || mappingValue?.value || "").trim()
      : String(mappingValue || "").trim();
    return mappedEntity.toLowerCase() === "global";
  };

  const splitRowsIntoChunks = (rows: Record<string, any>[], mappingDef: any) => {
    if (!rows.length) return [];
    const isMultiline = String(mappingDef?.entityMapping?.type || "").toLowerCase() === "multiline";
    if (!isMultiline) {
      return rows.map((row, rowIndex) => ({ rows: [row], rowIndices: [rowIndex] }));
    }

    const entityColumn = String(mappingDef?.entityMapping?.column || "").trim();
    if (!entityColumn) {
      return [{ rows: [...rows], rowIndices: rows.map((_, rowIndex) => rowIndex) }];
    }

    const chunks: Array<{ rows: Record<string, any>[]; rowIndices: number[] }> = [];
    let currentRows: Record<string, any>[] = [];
    let currentIndices: number[] = [];

    rows.forEach((row, rowIndex) => {
      const tokenValue = row?.[entityColumn];
      const isParent = isMultilineParentToken(mappingDef, tokenValue);
      if (isParent && currentRows.length > 0) {
        chunks.push({ rows: currentRows, rowIndices: currentIndices });
        currentRows = [];
        currentIndices = [];
      }
      currentRows.push(row);
      currentIndices.push(rowIndex);
    });

    if (currentRows.length > 0) {
      chunks.push({ rows: currentRows, rowIndices: currentIndices });
    }
    return chunks;
  };

  const mapCsvRowsWithFallback = async (
    fileContent: string,
    mapper: (rows: Record<string, any>[] | string) => Promise<any[]>,
  ): Promise<{ mapped: any[]; failedRows: CsvPreviewFailedRow[] }> => {
    const rows = await parseCSV(btoa(fileContent));
    if (!Array.isArray(rows) || rows.length === 0) {
      return { mapped: [], failedRows: [] };
    }

    try {
      const mapped = await mapper(fileContent);
      return { mapped, failedRows: [] };
    } catch {
      const chunks = splitRowsIntoChunks(rows, values);
      const mapped: any[] = [];
      const failedRows: CsvPreviewFailedRow[] = [];

      for (const chunk of chunks) {
        try {
          const chunkMapped = await mapper(chunk.rows);
          mapped.push(...chunkMapped);
        } catch (chunkError: any) {
          const message = chunkError instanceof Error ? chunkError.message : String(chunkError);
          chunk.rows.forEach((row, index) => {
            failedRows.push({
              row,
              rowNumber: (chunk.rowIndices[index] || 0) + 2,
              error: message,
            });
          });
        }
      }

      if (mapped.length === 0) {
        throw new Error(
          failedRows.length > 0
            ? `All rows failed mapping (${failedRows.length} rows).`
            : "Failed to process CSV",
        );
      }

      return { mapped, failedRows };
    }
  };

  const runPreviewMapping = async (fileName: string, fileContent: string) => {
    setIsProcessingCsv(true);
    setCsvPreview(null);
    try {
      const base64 = btoa(fileContent);
      let result: any;
      let failedRows: CsvPreviewFailedRow[] = [];

      if (previewType === "contact") {
        const mappedResult = await mapCsvRowsWithFallback(
          fileContent,
          async (rowsOrContent) => mapContactData(
            {
              ...values,
              mode: "csv",
              type: "contact",
            },
            release,
            rowsOrContent,
          ),
        );
        failedRows = mappedResult.failedRows;
        result = mappedResult.mapped.map((record) => {
          const id = record?.id ? String(record.id) : undefined;
          const payload = buildContactCreatePayload(record);
          return id ? { id, ...payload } : payload;
        });
      } else if (previewType === "entity") {
        const mappedResult = await mapCsvRowsWithFallback(
          fileContent,
          async (rowsOrContent) => mapEntityData(
            {
              ...values,
              mode: "csv",
              type: "entities",
            },
            release,
            rowsOrContent,
          ),
        );
failedRows = mappedResult.failedRows;
        result = mappedResult.mapped;
      } else if (previewType === "orgmodel") {
        const rows = await parseCSV(btoa(fileContent));
        const acceptedRows: Record<string, any>[] = [];
        const localFailedRows: CsvPreviewFailedRow[] = [];

        for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
          const row = rows[rowIndex];
          try {
            await mapOrgModelData(
              {
                ...values,
                mode: "csv",
                type: "orgmodels",
              },
              release,
              [...acceptedRows, row],
            );
            acceptedRows.push(row);
          } catch (rowError: any) {
            const message = rowError instanceof Error ? rowError.message : String(rowError);
            localFailedRows.push({
              row,
              rowNumber: rowIndex + 2,
              error: message,
            });
          }
        }

	        if (!acceptedRows.length && localFailedRows.length > 0) {
	          const firstError = localFailedRows[0]?.error;
	          throw new Error(
	            firstError
	              ? `All rows failed mapping (${localFailedRows.length} rows). First error: ${firstError}`
	              : `All rows failed mapping (${localFailedRows.length} rows).`,
	          );
	        }

        failedRows = localFailedRows;
        result = acceptedRows.length > 0
          ? await mapOrgModelData(
            {
              ...values,
              mode: "csv",
              type: "orgmodels",
            },
            release,
            acceptedRows,
          )
          : [];
      } else {
        result = await mapRequestData(
          {
            ...values,
            mode: "csv",
            type: "request" as any,
          },
          release,
          base64,
        );
      }

      setCsvPreview({
        file: fileName,
        result: result,
        originalData: fileContent,
        failedRows,
      });
    } catch (error: any) {
      console.error("CSV Preview error", error);
      setCsvPreview({
        file: fileName,
        result: null,
        originalData: fileContent,
        error: error.message || "Failed to process CSV",
      });
    } finally {
      setIsProcessingCsv(false);
    }
  };

  const canRefresh = Boolean(csvPreview?.originalData && csvPreview?.file);

  return (
    <div className="mt-6">
      <Typography variant="h5">CSV Preview</Typography>
      <Typography variant="body2" color="textSecondary" className="mb-4">
        Upload a CSV file to preview how the mapping will transform the data{previewType === "contact" ? " into contacts" : previewType === "entity" ? " into Extensions" : previewType === "orgmodel" ? " into org model records" : ""}
      </Typography>
      <FileDropZone
        onLoad={runPreviewMapping}
        accept=".csv"
        className="mb-4"
      />
      {isProcessingCsv && (
        <div className="flex items-center mt-4">
          <LoadingSpinner size="sm"  />
          <Typography variant="body2" className="ml-2">Processing CSV...</Typography>
        </div>
      )}
      {csvPreview && (
        <div className="p-4 mt-4 bg-white shadow-sm rounded border">
          <div className="mb-4 flex items-center justify-between gap-2">
            <Typography variant="subtitle2">
              Preview: {csvPreview.file}
            </Typography>
            <Button
              disabled={!canRefresh || isProcessingCsv}
              onClick={() => {
                if (!csvPreview?.file || !csvPreview?.originalData) return;
                runPreviewMapping(csvPreview.file, csvPreview.originalData);
              }}
              label="Refresh Preview"
            >
              Refresh Preview
            </Button>
          </div>
          {csvPreview.error ? (
            <Typography variant="body2" color="error">
              Error: {csvPreview.error}
            </Typography>
          ) : (
            <Tabs defaultValue="original" className="flex flex-col" style={{ minHeight: "400px", maxHeight: "600px" }}>
              {Array.isArray(csvPreview.failedRows) && csvPreview.failedRows.length > 0 && (
                <Typography variant="body2" color="warning.main" className="mb-2">
                  {csvPreview.failedRows.length} row(s) failed mapping and are shown in the Failed Rows tab.
                </Typography>
              )}
              <Tabs.List>
                <Tabs.Trigger value="original">Original CSV</Tabs.Trigger>
                <Tabs.Trigger value="mapped">{previewType === "contact" ? "Mapped Contacts" : previewType === "entity" ? "Mapped Entities" : previewType === "orgmodel" ? "Mapped Org Models" : "Mapped Result"}</Tabs.Trigger>
                {Array.isArray(csvPreview.failedRows) && csvPreview.failedRows.length > 0 && (
                  <Tabs.Trigger value="failed">Failed Rows ({csvPreview.failedRows.length})</Tabs.Trigger>
                )}
              </Tabs.List>
              <Tabs.Content value="original" className="flex-1 overflow-hidden">
                <div className="h-full" style={{ height: "400px" }}>
                  <SpreadsheetPreview
                    data={csvPreview.originalData || ""}
                    maxRows={100}
                  />
                </div>
              </Tabs.Content>
              <Tabs.Content value="mapped" className="flex-1 overflow-hidden">
                <div className="h-full overflow-auto" style={{ height: "400px" }}>
                  <JsonEditor
                    value={JSON.stringify(csvPreview.result, null, 2)}
                    readOnly={true}
                  />
                </div>
              </Tabs.Content>
              {Array.isArray(csvPreview.failedRows) && csvPreview.failedRows.length > 0 && (
                <Tabs.Content value="failed" className="flex-1 overflow-hidden">
                  <div className="h-full overflow-auto" style={{ height: "400px" }}>
                    <JsonEditor
                      value={JSON.stringify(csvPreview.failedRows, null, 2)}
                      readOnly={true}
                    />
                  </div>
                </Tabs.Content>
              )}
            </Tabs>
          )}
        </div>
      )}
    </div>
  );
};

interface CSVReadOnlyPreviewProps {
  csvData: string;
  maxRows?: number;
  title?: string;
}

export const CSVReadOnlyPreview = ({
  csvData,
  maxRows = 200,
  title,
}: CSVReadOnlyPreviewProps) => {
  return (
    <div className="flex flex-col h-full min-h-0">
      {title ? <Typography variant="subtitle1">{title}</Typography> : null}
      <div className="flex-1 min-h-0">
        <SpreadsheetPreview data={csvData} maxRows={maxRows} />
      </div>
    </div>
  );
};
